<template>
  <div v-el-resize="onResize" class="root" v-bind:class="{ active }">
    <canvas ref="canvas" class="d-block" :width="w" :height="h" />
  </div>
</template>


<style scoped>
.root {
  opacity: 0;
  pointer-events: none;
  transition: opacity 3s;
}
.root.active {
  opacity: 1;
}
</style>


<script>
import { processImageFile } from '@/utils/image.js';
import { EFFECTS } from '@/utils/skin.js';

// Logic is loosely based off this:
// http://jsfiddle.net/Javalsu/vxP5q/743/

// See this for ideas on how to use real images as particles:
// https://codepen.io/smhigley/pen/gwYPvR

const DEFAULT_OPTS = {
  density: .00015, // Number of particles per pixel (n_particles = w * h * density)
  colors: [ '#26ccff', '#a25afd', '#ff5e7e', '#88ff5a', '#fcff42', '#ffa62d', '#ff36ff' ],
  min_r: 10,
  max_r: 30,
  min_v: 200,
  max_v: 320,
  v_freq: .2, // Oscillations between min and max velocities per second (set to 0 for constant speed)
  drift: 20, // How much the particles move in the x direction
  drift_freq: .4, // Oscillations in the x direction per second
  max_tilt: 10, // Governs the distortion of the particles (set to 0 for none)
  tilt_freq: 1, // Oscillations in the tilt - higher numbers get really chaotic
  cap: 'butt' // Line cap style: butt, round, or square
};


function createParticle(w, h, opts, y0=undefined) {
  const x0 = Math.random() * w;
  if (y0 == undefined) {
    const maxY = h + opts.max_r + opts.max_tilt;
    y0 = (Math.random() * maxY) - maxY;
  }
  return {
    // These properties are used directly when drawing
    x: x0,
    y: y0,
    r: Math.floor(Math.random() * (opts.max_r - opts.min_r)) + opts.min_r,
    color: opts.colors[ Math.floor(Math.random() * opts.colors.length) ],
    tilt: 0,
    a: 1,

    // These properties just govern the internal state of the particle
    opts,
    x0,
    y0,
    dDriftRandom: Math.random(),
    dAngle: Math.random() * Math.PI * 2,
    vAngle: Math.random() * Math.PI * 2,
    tAngle: Math.random() * Math.PI * 2,
    fade: false
  };
}


export default {
  name: 'confetti',

  props: [ 'effect', 'active' ],

  data() {
    return {
      w: 0,
      h: 0
    }
  },

  computed: {
    opts() {
      const v = this.effect;
      if (!v)
        return DEFAULT_OPTS;
      // The 'effect' should be a serialized JSON blob containing the simpler user-configurable options like this:
      // {
      //   density: .5,
      //   size: .25,
      //   wind: .5,
      //   gravity: .75,
      //   colors: [ '#1E90FF', '#6b8E23' ]
      // }
      const cfg = EFFECTS[v] || JSON.parse(v);
      return {
        density: .00001 + .00099 * cfg.density,
        colors: cfg.colors,
        min_r: 3 + 20 * cfg.size,
        max_r: 6 + 56 * cfg.size,
        min_v: 90 - (40 * cfg.wind) + 400 * cfg.gravity,
        max_v: 110 + (40 * cfg.wind) + 400 * cfg.gravity,
        v_freq: .2 * cfg.wind,
        drift: 0 + 40 * cfg.wind,
        drift_freq: .01 + .9 * cfg.wind,
        max_tilt: 5 + 2 * cfg.gravity,
        tilt_freq: 1,
        cap: 'butt'
      };
    }
  },

  destroyed() {
    cancelAnimationFrame(this.animationFrame);
  },

  methods: {
    onResize({ width, height }) {
      this.w = width;
      this.h = height;
      this.reset();
    },

    reset() {
      cancelAnimationFrame(this.animationFrame);
      this.model = { tLastUpdate: null, particles: [] };
      const loop = () => {
        if (this.update()) {
          this.draw();
          this.animationFrame = requestAnimationFrame(loop);
        }
      };
      loop();
    },

    update() {
      const now = performance.now();
      const d = (now - (this.model.tLastUpdate || now)) / 1000; // Duration in seconds since last update
      this.model.tLastUpdate = now;

      const n = Math.ceil(this.w * 800 * this.opts.density);
      while (this.model.particles.length < n)
        this.model.particles.push(createParticle(this.w, this.h, this.opts));

      let nActive = this.model.particles.length;
      for (let i = 0; i < this.model.particles.length; i++) {
        const p = this.model.particles[i];
        const o = p.opts;

        p.vAngle += o.v_freq * d * 2 * Math.PI;
        p.y += o.min_v * d + ((o.max_v - o.min_v) * d * (1 + Math.sin(p.vAngle)) / 2);

        p.dAngle += o.drift_freq * d * 2 * Math.PI;
        p.x = p.x0 + o.drift * Math.sin(p.dAngle) * (1 + Math.sin(p.dDriftRandom));

        p.tAngle += o.tilt_freq * d * 2 * Math.PI;
        p.tilt = Math.sin(p.tAngle) * o.max_tilt;

        if (p.fade)
          p.a = Math.max(0, p.a - .01);

        if ((p.y >= this.h + p.r + p.tilt) || (p.a <= 0)) {
          if (this.active && i < n)
            this.model.particles[i] = createParticle(this.w, this.h, this.opts, p.y0);
          else
            nActive--;
        }
      }

      return nActive > 0;
    },

    // getImg(src) {
    //   if (!this.imgCache)
    //     this.imgCache = {};
    //   const cached = this.imgCache[src];
    //   if (!cached) {
    //     this.imgCache[src] = 1;
    //     fetch('/ellacard-logo-square.png')
    //       .then(r => r.blob())
    //       .then(file => processImageFile(file, 64, 64))
    //       .then(
    //         ({ url }) => {
    //           const img = new Image();
    //           img.onload = () => this.imgCache[src] = img;
    //           img.src = url;
    //         }
    //       );
    //     return null;
    //   } else if (cached == 1) {
    //     return null;
    //   } else {
    //     return cached;
    //   }
    // },

    draw() {
      const ctx = this.$refs.canvas.getContext('2d');
      ctx.clearRect(0, 0, this.w, this.h);
      for (const p of this.model.particles) {
        ctx.globalAlpha = p.a;
        ctx.beginPath();
        ctx.lineCap = p.opts.cap;
        ctx.lineWidth = p.r / 2;
        ctx.strokeStyle = p.color;
        ctx.moveTo(p.x + p.tilt + (p.r / 4), p.y);
        ctx.lineTo(p.x + p.tilt, p.y + p.tilt + (p.r / 4));
        ctx.stroke();
      }
    }
  },

  watch: {
    active: {
      immediate: true,
      async handler(v) {
        await this.$nextTick();
        if (v)
          this.reset();
      }
    },

    effect() {
      for (const p of this.model.particles)
        p.fade = true;
    }
  }
}
</script>
